import type {
  GeomEntity,
  Geometry,
  Vec2,
} from '../shared/types';
import { computeBounds } from './geometry';

/**
 * Generates the interior layout of a parametric sauna for a given footprint,
 * arranging the benches according to these rules:
 *
 *  - Benches are two-tier whenever the space allows.
 *  - The HIGH tier sits against the walls; the LOW tier sits toward the centre.
 *  - The high tier is 60 cm deep when feasible, otherwise 50 cm.
 *  - Benches form an L- or U-shape when the size allows; each tier joins around
 *    the corners with the same tier (high-with-high, low-with-low).
 *
 * Coordinates are in the sauna's local frame, centred on the origin, in mm.
 * The door is on the front (local -Y / bottom) wall.
 */

const WALK = 550; // preferred clear walkway (drives richer layouts)
const MIN_WALK = 300; // absolute minimum passage (last-resort single bench)

/** The only valid bench depths: 40, 50 or 60 cm (deepest first). */
const DEPTHS = [600, 500, 400];
const MIN_DEPTH = 400;
const ALT_DEPTH = 500; // used for side-bench width feasibility
const SIDE_MIN_DEPTH = 1100; // interior depth needed before side benches are added


const PASSAGE = 900; // desired clear passage past a bench end at the door (90 cm)
const MIN_LEG = 500; // keep at least this much side-bench length when trimming

/** The sauna door, as seen by the bench layout (position + opening width). */
export interface DoorInfo {
  offset: number;
  width: number;
}

export type SaunaLayoutName = 'single' | 'L-shape' | 'U-shape' | 'too-small';

export interface SaunaLayout {
  geometry: Geometry;
  layout: SaunaLayoutName;
  tiers: number;
  /** Tier depths in mm, outermost (against the wall) first. */
  depths: number[];
  /** High-tier depth in mm (0 if no benches). */
  high: number;
  /** Second-tier depth in mm (0 when single-tier). */
  low: number;
  /** Third-tier depth in mm (0 unless a third tier was added). */
  third: number;
  /** Total length of bench seating fronts (mm), counting tiers. */
  seatingLength: number;
  /** Capacity: one person per 60 cm of bench width across all tiers. */
  capacity: number;
}

/** Default sauna external (overall) height (mm): 210 cm for shallow saunas, 230 cm
 *  for deep. The interior clear height is this minus the ceiling thickness. */
export function defaultSaunaHeight(depth: number): number {
  return depth > 2500 ? 2300 : 2100;
}

interface Sides {
  left: boolean;
  right: boolean;
}
interface Ext {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export function buildSauna(width: number, depth: number, door?: DoorInfo, height?: number, wall = 62): SaunaLayout {
  const WALL = wall;
  const ix0 = -width / 2 + WALL;
  const ix1 = width / 2 - WALL;
  const iy0 = -depth / 2 + WALL;
  const iy1 = depth / 2 - WALL;
  const iw = ix1 - ix0;
  const id = iy1 - iy0;

  const empty = (): SaunaLayout => ({
    geometry: { entities: [], bounds: computeBounds([]) },
    layout: 'too-small',
    tiers: 0,
    depths: [],
    high: 0,
    low: 0,
    third: 0,
    seatingLength: 0,
    capacity: 0,
  });

  if (iw < 700 || id < 700) return empty();

  type Shape = 'U' | 'L' | 'single';
  const widthAllows = (shape: Shape): boolean => {
    if (shape === 'U') return iw >= 2 * ALT_DEPTH + WALK && id >= SIDE_MIN_DEPTH;
    if (shape === 'L') return iw >= ALT_DEPTH + WALK && id >= SIDE_MIN_DEPTH;
    return true;
  };

  // Greedily pick `count` tier depths (deepest first) that fit the available run
  // depth for a shape, leaving room for the remaining tiers at the minimum depth.
  const pickDepths = (shape: Shape, count: number, walk: number): number[] | null => {
    let m = id - walk; // back-wall constraint
    if (shape === 'U') m = Math.min(m, (iw - walk) / 2);
    else if (shape === 'L') m = Math.min(m, iw - walk);
    const depths: number[] = [];
    let remaining = m;
    for (let k = 0; k < count; k++) {
      const reserve = (count - 1 - k) * MIN_DEPTH;
      const d = DEPTHS.find((dd) => dd <= remaining - reserve);
      if (d === undefined) return null;
      depths.push(d);
      remaining -= d;
    }
    return depths;
  };

  // A third tier needs vertical room: only when the sauna is at least 230 cm tall.
  const tallEnough = (height ?? defaultSaunaHeight(depth)) >= 2300;
  const maxTiers = tallEnough ? 3 : 2;

  // The most tiers (up to maxTiers) a shape can fit, deepest-first.
  const fitBest = (shape: Shape, walk: number): number[] | null => {
    for (let tc = maxTiers; tc >= 1; tc--) {
      const d = pickDepths(shape, tc, walk);
      if (d) return d;
    }
    return null;
  };

  // Priority: U-shape (uses the whole perimeter) → for tall saunas a third tier
  // on the back beats spreading to an L-shape → L-shape → single bench.
  let chosen: { shape: Shape; depths: number[] } | null = null;
  if (widthAllows('U')) {
    const d = fitBest('U', WALK);
    if (d && d.length >= 2) chosen = { shape: 'U', depths: d };
  }
  if (!chosen && maxTiers >= 3) {
    const d = pickDepths('single', 3, WALK);
    if (d) chosen = { shape: 'single', depths: d };
  }
  if (!chosen && widthAllows('L')) {
    const d = fitBest('L', WALK);
    if (d && d.length >= 2) chosen = { shape: 'L', depths: d };
  }
  if (!chosen) {
    const d = fitBest('single', WALK);
    if (d && d.length >= 2) chosen = { shape: 'single', depths: d };
  }
  if (!chosen) {
    // Last resort: a single bench with a relaxed walkway (small saunas).
    for (const shape of ['single', 'L', 'U'] as Shape[]) {
      if (!widthAllows(shape)) continue;
      const d = pickDepths(shape, 1, MIN_WALK);
      if (d) { chosen = { shape, depths: d }; break; }
    }
  }
  if (!chosen) return empty();

  const { shape, depths } = chosen;
  const sides: Sides = { left: shape === 'U', right: shape === 'U' || shape === 'L' };

  // If the door is beside the short (front) side of a side bench, shorten that
  // bench from the front to leave a passage (90 cm if possible, otherwise less).
  let leftCut = 0;
  let rightCut = 0;
  if (door) {
    const dMin = door.offset - door.width / 2;
    const dMax = door.offset + door.width / 2;
    const legLen = id - depths[0]; // usable length of a side bench leg
    const cutFor = (benchMinX: number, benchMaxX: number): number => {
      const overlaps = dMax > benchMinX && dMin < benchMaxX;
      return overlaps ? Math.max(0, Math.min(PASSAGE, legLen - MIN_LEG)) : 0;
    };
    if (sides.left) leftCut = cutFor(ix0, ix0 + depths[0]);
    if (sides.right) rightCut = cutFor(ix1 - depths[0], ix1);
  }
  const leftFrontY = iy0 + leftCut;
  const rightFrontY = iy0 + rightCut;

  // Nested tier bands, outermost (against the walls) to innermost.
  const entities: GeomEntity[] = [];
  let seatingLength = 0;
  let inset = 0;
  for (const t of depths) {
    const ext: Ext = {
      x0: sides.left ? ix0 + inset : ix0,
      y0: iy0,
      x1: sides.right ? ix1 - inset : ix1,
      y1: iy1 - inset,
    };
    entities.push(bandPolygon(sides, ext, t, leftFrontY, rightFrontY));
    seatingLength += ext.x1 - ext.x0; // back run (full width)
    if (sides.left) seatingLength += Math.max(0, ext.y1 - t - leftFrontY);
    if (sides.right) seatingLength += Math.max(0, ext.y1 - t - rightFrontY);
    inset += t;
  }
  seatingLength = Math.max(0, seatingLength);

  // The heater is a separate product the user drags onto the sauna (it becomes a
  // grouped instance), so the parametric layout no longer draws one itself.

  return {
    geometry: { entities, bounds: computeBounds(entities) },
    layout: shape === 'U' ? 'U-shape' : shape === 'L' ? 'L-shape' : 'single',
    tiers: depths.length,
    depths,
    high: depths[0] || 0,
    low: depths[1] || 0,
    third: depths[2] || 0,
    seatingLength,
    // One person per 60 cm of bench width, across all tiers.
    capacity: Math.floor(seatingLength / 600),
  };
}

/**
 * Builds a bench band that runs along the back wall (always) and optionally the
 * left/right walls, joined around the corners. `ext` gives the outer (wall-side)
 * extent and `t` the band depth toward the centre. The door wall (y0) is open.
 */
function bandPolygon(
  sides: Sides,
  ext: Ext,
  t: number,
  leftFrontY: number,
  rightFrontY: number
): GeomEntity {
  const { x0, y0, x1, y1 } = ext;
  const ix = sides.left ? x0 + t : x0; // inner left edge
  const rx = sides.right ? x1 - t : x1; // inner right edge
  const by = y1 - t; // inner back edge
  const lf = sides.left ? leftFrontY : y0; // (possibly trimmed) left-leg front
  const rf = sides.right ? rightFrontY : y0; // (possibly trimmed) right-leg front
  const pts: Vec2[] = [];

  // Outer path along the benched walls.
  if (sides.left) pts.push({ x: x0, y: lf });
  pts.push({ x: x0, y: y1 });
  pts.push({ x: x1, y: y1 });
  if (sides.right) pts.push({ x: x1, y: rf });

  // Inner path back toward the start.
  if (sides.right) {
    pts.push({ x: rx, y: rf });
    pts.push({ x: rx, y: by });
  } else {
    pts.push({ x: x1, y: by });
  }
  pts.push({ x: ix, y: by });
  if (sides.left) pts.push({ x: ix, y: lf });

  return { kind: 'polyline', closed: true, points: pts };
}
